import React from 'react';
import { Link } from 'react-router-dom';
import LegalClause from './LegalClause';

/**
 * Privacy Policy body content — see TermsContent.tsx for the shared
 * shape (no page chrome, `onOpenTerms` swaps cross-references to an
 * in-modal action instead of a route navigation). Content transcribed
 * from docs/legal/privacy-policy.md, which is the source of truth.
 */
interface PrivacyContentProps {
  onOpenTerms?: () => void;
}

export default function PrivacyContent({ onOpenTerms }: PrivacyContentProps) {
  const termsLink = onOpenTerms ? (
    <button
      type="button"
      onClick={onOpenTerms}
      className="text-legal-green-700 dark:text-legal-green-400 underline"
    >
      Terms of Service
    </button>
  ) : (
    <Link to="/terms" className="text-legal-green-700 dark:text-legal-green-400 underline">
      Terms of Service
    </Link>
  );

  return (
    <>
      <p className="text-xs tracking-[0.14em] uppercase text-legal-green-700 dark:text-legal-green-400 mb-2">
        Privacy Policy
      </p>
      <h1 className="text-3xl font-semibold text-text-primary mb-3">iAdvocate Privacy Policy</h1>
      <p className="text-sm text-text-secondary mb-8">
        Operated by <strong>Kovai Digi Tech</strong> under the brand <strong>iAdvocate</strong>, at{' '}
        <strong>iadvocate.ai</strong>. Last updated 3 November 2026. Read together with our{' '}
        {termsLink}.
      </p>

      <LegalClause id="p1" n="1" title="Two Kinds of Personal Data">
        <p><strong>1.1 Your data</strong> as a registered User: name, email, phone number, practice area(s), usage/billing data.</p>
        <p><strong>1.2 Client Data you upload</strong>, inside Matter Content, may include personal data of your clients or others. <strong>You are the Data Fiduciary</strong> under the DPDP Act for that data; Kovai Digi Tech acts only as your Data Processor.</p>
      </LegalClause>

      <LegalClause id="p2" n="2" title="What We Collect and Why">
        <p>Account data (name, email, Firebase identifier, phone) — to create and secure your account. Profile data (practice areas, tier) — to personalise the workspace. Matter Content — to provide search, AI analysis, and drafting. Usage data — product improvement, billing, abuse prevention. Billing data — process payments (payment instrument details are held by our payment gateway, not by us).</p>
        <p id="support-outreach"><strong>2.1 Support outreach.</strong> Our support team may call or WhatsApp you, using the phone number on your account (or email you), to help you get started or to follow up on a problem. To decide whom to contact and what to say, authorised support staff can see your account details and basic usage information, for example whether you have added a case, when you last used the product, and that an error occurred for you. They do not see the contents of your matters, your documents or your clients&rsquo; details through this tool, though they may see counts and dates, such as that a hearing date is approaching. Each time support staff view your profile or record a contact, we log which staff member did so and when. You can ask us to stop contacting you at any time by writing to support@iadvocate.ai.</p>
      </LegalClause>

      <LegalClause id="p3" n="3" title="How AI Processing Works With Your Data">
        <p>3.1 AI-assisted features send relevant Matter Content to Google Gemini (analysis) and Google Gemini File Search / BigQuery (legal search).</p>
        <p>3.2 <strong>We do not use your Matter Content or Client Data to train third-party foundation models.</strong> Our AI processing runs on Google&rsquo;s Gemini API under a paid subscription. Under Google&rsquo;s <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-legal-green-700 dark:text-legal-green-400 underline">Gemini API Terms of Service</a> (Paid Services), prompts and responses submitted through paid API quota are not used to improve or train Google&rsquo;s models — they are processed only to provide the service to us, detect abuse, and maintain security.</p>
        <p>3.3 AI Output can be wrong — see Terms of Service Section 6. Always independently verify before relying on it.</p>
      </LegalClause>

      <LegalClause id="p4" n="4" title="Sub-processors">
        <p>Google Firebase (authentication), Google Gemini API / File Search (AI analysis, search), Google Cloud Storage (document vault), Google BigQuery (judgment search), and our payment gateway (billing). We do not sell your personal data or Client Data.</p>
        <p id="messaging"><strong>Messaging (WhatsApp / SMS / calls).</strong> iAdvocate does not currently send automated WhatsApp or SMS messages to you or to your clients, and does not share your clients&rsquo; phone numbers or any Matter Content with a messaging provider. Separately, our support team may contact you personally by phone call or WhatsApp (see Section 2.1), using the phone number on your account; a WhatsApp message to you is carried by WhatsApp (Meta) under its own terms. Before any automated messaging is switched on, this Policy will be updated to say what is sent and on whose instruction, how consent is recorded and withdrawn (opt-out), and what is shared with the messaging provider (for WhatsApp, Meta).</p>
      </LegalClause>

      <LegalClause id="p5" n="5" title="Retention and Deletion">
        <p>5.1 Account data is retained while your account is active; Matter Content for as long as you keep it in the platform.</p>
        <p>5.2 You may delete individual Matter Content or your entire account at any time; associated personal data is deleted or anonymised within a reasonable period, except where retention is legally required (e.g. billing records).</p>
      </LegalClause>

      <LegalClause id="p6" n="6" title="Your Rights Under the DPDP Act">
        <p>You may access, correct, or erase your personal data; withdraw consent at any time; nominate someone to exercise these rights on your behalf; and raise a grievance with our Grievance Officer, or the Data Protection Board of India if unresolved.</p>
      </LegalClause>

      <LegalClause id="p7" n="7" title="Security">
        <p>We use encryption in transit, access controls, and tenant isolation by organisation. No system is perfectly secure; we will notify you and the relevant authority of any breach affecting your personal data as required by law.</p>
      </LegalClause>

      <LegalClause id="p8" n="8" title="Grievance Officer / Contact">
        <p>Grievance Officer: <strong>grievances@iadvocate.ai</strong></p>
        <p>Privacy queries: <strong>support@iadvocate.ai</strong></p>
      </LegalClause>

      <LegalClause id="p9" n="9" title="Children">
        <p>The Service is intended for legal professionals and adults (18+). We do not knowingly collect data from children.</p>
      </LegalClause>

      <LegalClause id="p10" n="10" title="Changes to This Policy">
        <p>Material changes require you to re-accept, in the same manner as Terms of Service changes.</p>
      </LegalClause>
    </>
  );
}
